// src/resources/customerRfm/SegmentPlaybook.tsx
//
// "O que fazer com este grupo" (spec §6.2 item 5): playbook do segmento
// selecionado com as 4 ações — etiquetar, criar desconto, pontuar e exportar
// CSV. As ações agem sobre os FILTROS atuais da tela (segmento, célula do
// mapa, fluxo, 💎, posto, busca), não sobre uma seleção manual de linhas.
import React, { useState } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import jsonExport from 'jsonexport/dist';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { downloadCSV, useNotify, usePermissions } from 'react-admin';
import { Box, Button, CircularProgress, Tooltip, Typography } from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import DiscountIcon from '@mui/icons-material/Discount';
import StarsIcon from '@mui/icons-material/Stars';
import GetAppIcon from '@mui/icons-material/GetApp';
import { __t } from '../../i18n/t';
import { PERMISSIONS } from '../../constants';
import { formatCellphone, formatCpf, formatPartiallyHiddenCpf } from '../../utils';
import { COLORS, EmptyHint, cardSx, fmtNum } from '../dashboards/shared';
import { RfmFilters, fetchRfmCustomerIds, fetchRfmCustomers } from './api';
import { ListOrigin } from '../discounts/v2/types';
import { RfmSegment, SEGMENT_META } from './segments';
import TagSegmentDialog from './TagSegmentDialog';

const MAX_BULK_ACTION = 50000;
const CSV_CAP = 20000;

interface Props {
  filters: RfmFilters; // já inclui chainId
  segment?: RfmSegment;
  previousSegment?: RfmSegment | 'new';
  filteredTotal: number;
}

const SegmentPlaybook: React.FC<Props> = ({ filters, segment, previousSegment, filteredTotal }) => {
  const navigate = useNavigate();
  const notify = useNotify();
  const { permissions } = usePermissions();
  const canTag = !!permissions?.includes(PERMISSIONS.TAG);
  const isAdmin = !!permissions?.includes(PERMISSIONS.ADMIN);

  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [redirecting, setRedirecting] = useState<'discount' | 'scoring' | null>(null);
  const [exporting, setExporting] = useState(false);

  const meta = segment ? SEGMENT_META[segment] : null;
  const isNewOnly = !segment && previousSegment === 'new';

  if (!meta && !isNewOnly) {
    return (
      <Box sx={{ ...cardSx }}>
        <EmptyHint text="Escolha um grupo acima (card, célula do mapa ou fluxo) para ver o que fazer com ele." />
      </Box>
    );
  }

  const overCap = filteredTotal > MAX_BULK_ACTION;
  const { chainId, ...filterOnly } = filters;

  const goCreate = async (kind: 'discount' | 'scoring') => {
    setRedirecting(kind);
    try {
      const { ids, truncated } = await fetchRfmCustomerIds(filters);
      if (truncated) {
        notify('Mais de 50.000 clientes no filtro — usando os 50.000 primeiros por valor.', { type: 'warning' });
      }
      const path = kind === 'discount' ? '/discounts/create' : '/scorings/create';
      // `origin` deixa o cadastro de desconto dizer "Painel 360" só para quem veio de lá
      navigate(path, { state: { customerIds: ids, type: 'special', origin: ListOrigin.rfm } });
    } catch {
      notify('Não foi possível preparar os clientes para essa ação. Tente de novo.', { type: 'warning' });
    } finally {
      setRedirecting(null);
    }
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const { data, total } = await fetchRfmCustomers(filters, { skip: 0, take: CSV_CAP, orderBy: 'totalValue', orderDir: 'DESC' });
      if (total > CSV_CAP) {
        notify(`O grupo tem ${fmtNum(total)} clientes — exportando os ${fmtNum(CSV_CAP)} primeiros por valor.`, { type: 'warning' });
      }
      // i18n: valores de célula do CSV (não são JSX, o plugin de build não embrulha) —
      // __t explícito pra sair em espanhol quando a rede for hispanofalante. Os nomes
      // de coluna (chave do objeto) ficam em português, mesmo padrão dos outros exports.
      const rows = data.map((c) => ({
        nome: c.name,
        cpf: c.cpf ? (isAdmin ? formatCpf(c.cpf) : (c.cpf.includes('*') ? c.cpf : formatPartiallyHiddenCpf(c.cpf))) : '—',
        celular: c.cellphone ? formatCellphone(c.cellphone) : '—',
        grupo: __t(SEGMENT_META[c.segment].label),
        grupoHa30Dias: c.previousSegment ? __t(SEGMENT_META[c.previousSegment].label) : __t('Novo'),
        altoValor: c.highValue ? __t('Sim') : __t('Não'),
        ultimoAbastecimento: dayjs(c.lastFillinAt).format('DD/MM/YYYY HH:mm'),
        diasSemAbastecer: c.daysSinceLast,
        abastecimentos12m: c.fillinCount,
        valor12m: c.totalValue,
        postoPrincipal: c.mainPlaceName || '—',
        etiquetas: (c.tags || []).map((t) => t.name).join(', '),
      }));
      jsonExport(rows, (_err: any, csv: string) => downloadCSV(csv, `segmentos-valor-rfm-${segment || 'novos'}`));
    } catch {
      notify('Não foi possível exportar. Tente de novo.', { type: 'warning' });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ ...cardSx }}>
      {/* i18n: template literal dentro de ternário não é embrulhado pelo plugin de build, e
          meta.label/.playbook/.suggestion são valores crus (não passam pelo tradutor sozinhos)
          — __t explícito em cada um. */}
      <Typography sx={{ fontSize: 14, fontWeight: 700, color: meta?.color ?? COLORS.ink }}>
        {meta
          ? __t('%{emoji} O que fazer com o grupo %{label}', { emoji: meta.emoji, label: __t(meta.label) })
          : __t('🆕 O que fazer com os clientes novos')}
      </Typography>
      <Typography sx={{ fontSize: 12.5, color: COLORS.ink, mt: 0.75 }}>
        {meta ? __t(meta.playbook) : __t('Chegaram (ou voltaram) nos últimos 30 dias e ainda não tinham um grupo de RFM. A 2ª visita decide se ficam.')}
      </Typography>
      <Typography sx={{ fontSize: 12, color: COLORS.muted, mt: 0.5, mb: 1.75 }}>
        💡 {meta ? __t(meta.suggestion) : __t('Notificação de boas-vindas ou desconto na próxima visita.')}
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <Tooltip title={canTag ? '' : 'Peça a permissão de etiquetas para usar esta ação.'} arrow>
          <span>
            <Button
              variant="outlined" size="small" startIcon={<LocalOfferIcon fontSize="small" />}
              disabled={!canTag}
              onClick={() => setTagDialogOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
            >
              Etiquetar estes clientes
            </Button>
          </span>
        </Tooltip>

        <Tooltip title={overCap ? 'Filtre mais: posto principal, 💎 ou uma célula do mapa.' : ''} arrow>
          <span>
            <Button
              variant="outlined" size="small" startIcon={redirecting === 'discount' ? <CircularProgress size={14} /> : <DiscountIcon fontSize="small" />}
              disabled={overCap || redirecting !== null}
              onClick={() => goCreate('discount')}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
            >
              {__t('Criar desconto para %{n}', { n: fmtNum(filteredTotal) })}
            </Button>
          </span>
        </Tooltip>

        <Tooltip title={overCap ? 'Filtre mais: posto principal, 💎 ou uma célula do mapa.' : ''} arrow>
          <span>
            <Button
              variant="outlined" size="small" startIcon={redirecting === 'scoring' ? <CircularProgress size={14} /> : <StarsIcon fontSize="small" />}
              disabled={overCap || redirecting !== null}
              onClick={() => goCreate('scoring')}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
            >
              {__t('Pontuar %{n} clientes', { n: fmtNum(filteredTotal) })}
            </Button>
          </span>
        </Tooltip>

        <Button
          variant="text" size="small" startIcon={exporting ? <CircularProgress size={14} /> : <GetAppIcon fontSize="small" />}
          disabled={exporting}
          onClick={handleExportCsv}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
        >
          Exportar CSV
        </Button>
      </Box>

      <TagSegmentDialog
        open={tagDialogOpen}
        onClose={() => setTagDialogOpen(false)}
        chainId={chainId}
        segment={segment}
        filter={filterOnly}
        estimatedTotal={filteredTotal}
      />
    </Box>
  );
};

export default SegmentPlaybook;
